/**
 * IP地理位置检测工具
 * 用于根据用户的IP地址自动检测国家/地区
 */

/**
 * 通过IP地址获取用户地理位置信息
 * @returns {Promise<{country: string, countryCode: string, region: string}|null>} 地理位置信息或null
 */
export async function detectLocationByIP() {
  try {
    // 使用 ipapi.co 免费API（无需API密钥，但有速率限制）
    // 如果失败，可以尝试其他免费服务如 ip-api.com
    // 使用 AbortController 实现超时控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error('IP geolocation API failed');
    }

    const data = await response.json();
    
    // ipapi.co 返回格式: { country_name, country_code, region, ... }
    if (data && data.country_name) {
      return {
        country: data.country_name,
        countryCode: data.country_code || '',
        region: data.region || '',
        city: data.city || ''
      };
    }

    return null;
  } catch (error) {
    // 如果第一个API失败，尝试使用 ip-api.com 作为备用
    try {
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(() => fallbackController.abort(), 5000);
      
      const fallbackResponse = await fetch('http://ip-api.com/json/?fields=country,countryCode,region,city', {
        method: 'GET',
        signal: fallbackController.signal
      });
      
      clearTimeout(fallbackTimeoutId);

      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        if (fallbackData && fallbackData.country) {
          return {
            country: fallbackData.country,
            countryCode: fallbackData.countryCode || '',
            region: fallbackData.region || '',
            city: fallbackData.city || ''
          };
        }
      }
    } catch (fallbackError) {
      // 备用API也失败，返回null
    }

    return null;
  }
}

/**
 * 根据地理位置信息匹配国家代码
 * @param {Array} countryOptions - 国家选项数组，格式：[{ value: string, text: string, area: string, countryId: number }]
 * @param {string} detectedCountry - 检测到的国家名称
 * @param {string} detectedCountryCode - 检测到的国家代码（ISO 2字母代码，如 "HK"）
 * @returns {string|null} 匹配的国家代码（如 "+852"）或null
 */
export function matchCountryCodeByLocation(countryOptions, detectedCountry, detectedCountryCode) {
  if (!countryOptions || countryOptions.length === 0) {
    return null;
  }

  if (!detectedCountry && !detectedCountryCode) {
    return null;
  }

  // 国家名称映射表（处理不同API可能返回的不同名称）
  // 注意：不同语言下，国家名称可能不同，需要包含各种可能的名称
  const countryNameMappings = {
    'Hong Kong': ['HONG KONG', 'HONG KONG (CHINA)', 'Hong Kong SAR China', 'Hong Kong', 'HK', '香港', '中國香港', '中国香港', 'HKO'],
    'China': ['CHINA', 'China', 'CN', 'Mainland China', '中国', '中國', 'PRC'],
    'United States': ['UNITED STATES', 'United States', 'USA', 'US', 'America'],
    'United Kingdom': ['UNITED KINGDOM', 'United Kingdom', 'UK', 'GB', 'Great Britain'],
    'Japan': ['JAPAN', 'Japan', 'JP'],
    'South Korea': ['SOUTH KOREA', 'South Korea', 'Korea', 'KR'],
    'Singapore': ['SINGAPORE', 'Singapore', 'SG'],
    'Taiwan': ['TAIWAN', 'Taiwan', 'TW', 'Taiwan, Province of China'],
    'Macau': ['MACAU', 'Macau', 'MO', 'Macao', 'Macao SAR China'],
    'Malaysia': ['MALAYSIA', 'Malaysia', 'MY'],
    'Thailand': ['THAILAND', 'Thailand', 'TH'],
    'India': ['INDIA', 'India', 'IN'],
    'Australia': ['AUSTRALIA', 'Australia', 'AU'],
    'Canada': ['CANADA', 'Canada', 'CA'],
    'Germany': ['GERMANY', 'Germany', 'DE'],
    'France': ['FRANCE', 'France', 'FR'],
    'Italy': ['ITALY', 'Italy', 'IT'],
    'Spain': ['SPAIN', 'Spain', 'ES'],
    'Netherlands': ['NETHERLANDS', 'Netherlands', 'NL'],
    'Brazil': ['BRAZIL', 'Brazil', 'BR'],
    'Mexico': ['MEXICO', 'Mexico', 'MX'],
    'Russia': ['RUSSIA', 'Russian Federation', 'RU'],
    'Indonesia': ['INDONESIA', 'Indonesia', 'ID'],
    'Philippines': ['PHILIPPINES', 'Philippines', 'PH'],
    'Vietnam': ['VIETNAM', 'Vietnam', 'VN']
  };

  // 特殊处理：香港的国家代码映射
  const specialCountryCodeMappings = {
    'HK': 'HONG KONG',
    'CN': 'CHINA',
    'MO': 'MACAU',
    'TW': 'TAIWAN'
  };

  // 标准化检测到的国家名称（转为大写）
  const normalizedDetectedCountry = (detectedCountry || '').toUpperCase().trim();
  const normalizedDetectedCode = (detectedCountryCode || '').toUpperCase().trim();

  // 首先尝试通过国家代码匹配（更准确）
  if (normalizedDetectedCode) {
    // 检查特殊映射
    const mappedCountryName = specialCountryCodeMappings[normalizedDetectedCode];
    if (mappedCountryName) {
      // 尝试多种匹配方式
      let matched = countryOptions.find(opt => {
        const optText = (opt.text || '').toUpperCase();
        return optText.includes(mappedCountryName);
      });
      
      // 如果没找到，尝试通过区号匹配（例如 HK -> +852）
      if (!matched && normalizedDetectedCode === 'HK') {
        matched = countryOptions.find(opt => opt.value === '+852');
      }
      
      if (matched) {
        return matched.value;
      }
    }
  }

  // 然后尝试直接匹配国家名称
  if (normalizedDetectedCountry) {
    // 检查映射表
    const possibleNames = countryNameMappings[detectedCountry] || [normalizedDetectedCountry];
    
    for (const name of possibleNames) {
      const matched = countryOptions.find(opt => {
        const optText = (opt.text || '').toUpperCase();
        // 精确匹配或包含匹配
        return optText === name.toUpperCase() || optText.includes(name.toUpperCase());
      });
      
      if (matched) {
        return matched.value;
      }
    }

    // 如果映射表中没有，尝试模糊匹配
    let matched = countryOptions.find(opt => {
      const optText = (opt.text || '').toUpperCase();
      // 检查是否包含检测到的国家名称，或者检测到的国家名称包含选项文本
      return optText.includes(normalizedDetectedCountry) || 
             normalizedDetectedCountry.includes(optText.split(' ')[0]); // 匹配第一个单词
    });
    
    // 如果还是没找到，尝试通过区号匹配（针对香港的特殊情况）
    if (!matched && (normalizedDetectedCountry.includes('HONG') || normalizedDetectedCountry.includes('HK'))) {
      matched = countryOptions.find(opt => opt.value === '+852');
    }
    
    if (matched) {
      return matched.value;
    }
  }

  return null;
}

/**
 * 自动检测并匹配国家代码
 * @param {Array} countryOptions - 国家选项数组
 * @returns {Promise<string|null>} 匹配的国家代码（如 "+852"）或null
 */
export async function autoDetectCountryCode(countryOptions) {
  try {
    // 检测地理位置
    const locationInfo = await detectLocationByIP();
    
    if (!locationInfo) {
      return null;
    }

    // 匹配国家代码
    const matchedCode = matchCountryCodeByLocation(
      countryOptions,
      locationInfo.country,
      locationInfo.countryCode
    );

    return matchedCode;
  } catch (error) {
    return null;
  }
}

